import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import type { PriceImportRun } from './types';
import { priceImportsEndpointIdentifier } from './use-get-price-import-runs-api';

/**
 * Upload only. The BE deliberately does NO inference here — it stores the file,
 * reads its shape and records the run — so this call is fast. The slow work is
 * POST /:id/adapt, split out so no single request holds file I/O, an inference
 * round trip and the matching pass at once.
 */
type TData = { data: PriceImportRun };
type TError = AxiosError;
type TVariables = { supplierId: number; file: File; sheetName?: string };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useCreatePriceImportRunApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ supplierId, file, sheetName }: TVariables) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('supplierId', String(supplierId));
    if (sheetName) formData.append('sheetName', sheetName);

    try {
      const { data } = await http.post(priceImportsEndpointIdentifier, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [priceImportsEndpointIdentifier],
    // Deliberately no retry: a slow upload must not be fired twice.
    retry: false,
    ...options,
  });
}
